export type FeatureFlagDefaultStringVariant = 'UNASSIGNED' | 'ENABLED' | 'DISABLED'

type TimelineStage =
  | 'PRE_SEASON'
  | 'TAX_SEASON_START'
  | 'EMPLOYER_FORMS_SENT'
  | 'INVESTMENT_FORMS_SENT'
  | 'EFILE_DEADLINE'
  | 'POST_SEASON'

export type TaxesArticleName =
  | 'how-to-check-your-refund'
  | 'refund-tips'
  | 'things-to-know'
  | 'what-goes-into-your-refund'
  | 'whats-new'
  | 'what-to-do-if-you-owe'
  | 'why-cash-app-taxes'

export type ArcadeComponentMigrationFlags =
  | 'cash-web-arcade-migration-apps'
  | 'cash-web-arcade-migration-button'
  | 'cash-web-arcade-migration-input'
  | 'cash-web-arcade-migration-padding-inset'
  | 'cash-web-arcade-migration-avatar'
  | 'cash-web-arcade-migration-loading-image'
  | 'cash-web-arcade-migration-loading-icon'
export type ArcadeComponentMigrationConfig = { [K in ArcadeComponentMigrationFlags]?: FeatureFlagDefaultStringVariant }

export enum SeverityLevel {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
}

export type OutageBannerIncident = {
  details?: string
  id?: string
  reportedAt?: number
  severityLevel?: SeverityLevel
  status?: string
  title?: string
}

export type Incidents = {
  incidents?: OutageBannerIncident[]
}

export type AccountSwitcherVersion = {
  minAndroidAppVersion: string
  minIOSAppVersion: string
}

type RumConfig = { sessionSampleRateOverride: number }

type TaxesBtcImportTopOfFunnelType = {
  enabled?: boolean
  taxesAppletBannerCopy?: string
}

export type FeatureFlags = {
  'cash-frontend-admin-rollouts': boolean
  'cash-frontend-product-referrals-friendsgiving': boolean
  'cash-frontend-product-resend-code': boolean
  'cash-frontend-service-default-outage-status-message': string
  'cash-frontend-tax-upgrade': boolean
  'cash-web-3ds-redirect-url-test': string
  'cash-web-account-activity-download': boolean
  'cash-web-account-activity-filter-domain-type': FeatureFlagDefaultStringVariant
  'cash-web-account-address-autocomplete': FeatureFlagDefaultStringVariant
  'cash-web-account-ai-playground': boolean
  'cash-web-account-allow-international-p2p': FeatureFlagDefaultStringVariant
  'cash-web-account-bitcoin-limits': boolean
  'cash-web-account-datadog-rum-config': RumConfig
  'cash-web-account-datadog-rum-enabled': boolean
  'cash-web-account-edit-distributions': FeatureFlagDefaultStringVariant
  'cash-web-account-enable-card-studio-touch-devices': boolean
  'cash-web-account-enable-multi-allocation-blocker': boolean
  'cash-web-account-enable-paychecks-stock-editing': boolean
  'cash-web-account-enable-sid': boolean
  'cash-web-account-enable-tm': boolean
  'cash-web-account-filter-money-movement': FeatureFlagDefaultStringVariant
  'cash-web-account-initiate-cash-out-endpoint': FeatureFlagDefaultStringVariant
  'cash-web-account-multiple-goals-paychecks-enabled': boolean
  'cash-web-account-new-sam-settings': boolean
  'cash-web-account-onboarding-validator': 'UNASSIGNED' | 'REQUIRE_REGION_ONLY' | 'REQUIRE_ALL'
  'cash-web-account-p2p-payment-flow-client-scenario': FeatureFlagDefaultStringVariant
  'cash-web-account-paychecks': FeatureFlagDefaultStringVariant
  'cash-web-account-paychecks-nux': boolean
  'cash-web-account-pools-testing': boolean
  'cash-web-account-roundup-upsells': boolean
  'cash-web-account-sam': FeatureFlagDefaultStringVariant
  'cash-web-account-savings-fetch-sponsorship': boolean
  'cash-web-account-savings-interactive': boolean
  'cash-web-account-savings-nux': boolean
  'cash-web-account-settings-instrument-link-overrides': {
    allowBankLink?: boolean
    allowCreditLink?: boolean
    allowDebitLink?: boolean
  }
  'cash-web-account-settings-show-devices': boolean
  'cash-web-account-show-card-personalizations': boolean
  'cash-web-account-show-if-block-employee': boolean
  'cash-web-account-show-money-tab-uk': FeatureFlagDefaultStringVariant
  'cash-web-account-show-p2p-for-uk': FeatureFlagDefaultStringVariant
  'cash-web-account-taxes-banner': FeatureFlagDefaultStringVariant
  'cash-web-account-taxes-entrypoint-in-sidebar-nav': FeatureFlagDefaultStringVariant
  'cash-web-account-taxes-returns-entrypoint-in-sidebar-documents-nav': FeatureFlagDefaultStringVariant
  'cash-web-account-verify-alias-additional-help-items': boolean
  'cash-web-activity-cell': boolean
  'cash-web-admin-datadog-rum-config': RumConfig
  'cash-web-arcade-feature-mobile-painted-door': FeatureFlagDefaultStringVariant
  'cash-web-arcade-migration': ArcadeComponentMigrationConfig
  'cash-web-bot-prompts-you': boolean
  'cash-web-business-tax-information-react': FeatureFlagDefaultStringVariant
  'cash-web-cashtags-datadog-rum-config': RumConfig
  'cash-web-cashtags-local-enable-cash-app-pay': boolean // Temporary: Can remove when we appropriately gate based on the logged in user.
  'cash-web-cashtags-local-enable-login': boolean
  'cash-web-cashtags-p2p-enable-pay-links': boolean
  'cash-web-confirmation-claim-local-cash': string
  'cash-web-conversion-tracking': boolean
  'cash-web-device-details-endpoint': boolean
  'cash-web-enable-risk-signal': boolean
  'cash-web-enable-sid-retry': boolean
  'cash-web-enable-sitm': boolean
  'cash-web-enable-tm': boolean
  'cash-web-enable-wait-for-sid': boolean
  'cash-web-grant-flow-datadog-rum-config': RumConfig
  'cash-web-grant-flow-enable-login-close': boolean
  'cash-web-grant-flow-enable-monochrome': boolean
  'cash-web-grant-flow-inline-auth': boolean
  'cash-web-incidents': Incidents
  'cash-web-lightning-datadog-rum-config': RumConfig
  'cash-web-local-profile-datadog-rum-config': RumConfig
  'cash-web-local-profile-use-one-trust': boolean
  'cash-web-login-convergence-skip-login': boolean
  'cash-web-login-datadog-rum-config': RumConfig
  'cash-web-moneybot-background-connection': boolean
  'cash-web-moneybot-cached-session-id-timeout-seconds': number
  'cash-web-moneybot-debug-copy-session': boolean
  'cash-web-moneybot-developer-config': boolean
  'cash-web-neighborhoods-enable-tm': boolean
  'cash-web-neighborhoods-local-page-v3': boolean
  'cash-web-one-trust-banner-visibility': boolean
  'cash-web-p2p-allow-new-aliases': FeatureFlagDefaultStringVariant
  'cash-web-p2p-allow-search-new-recipients': FeatureFlagDefaultStringVariant
  'cash-web-p2p-misdirected-payment-in-flow': FeatureFlagDefaultStringVariant
  'cash-web-passkeys': FeatureFlagDefaultStringVariant
  'cash-web-passkeys-try-login-with-passkeys': FeatureFlagDefaultStringVariant
  'cash-web-personalized-ads-enabled': boolean
  'cash-web-pools-datadog-rum-config': RumConfig
  'cash-web-pools-enable-contributions': boolean
  'cash-web-replace-cash-card-ui': FeatureFlagDefaultStringVariant
  'cash-web-shadow-account-debit-text': FeatureFlagDefaultStringVariant
  'cash-web-show-money-tab-settings': { countries?: ReadonlyArray<string> }
  'cash-web-standalone-pages-datadog-rum-config': RumConfig
  'cash-web-standalone-pools-enriched-receipt': boolean
  'cash-web-support-datadog-rum-config': RumConfig
  'cash-web-support-datadog-rum-enabled': boolean
  'cash-web-support-show-search': boolean
  'cash-web-taxes-account-switcher-version': AccountSwitcherVersion
  'cash-web-taxes-article-list': Record<TaxesArticleName, boolean>
  'cash-web-taxes-btc-import-top-of-funnel': TaxesBtcImportTopOfFunnelType
  'cash-web-taxes-datadog-rum-config': RumConfig
  'cash-web-taxes-desktop-applet': FeatureFlagDefaultStringVariant
  'cash-web-taxes-experience-service': boolean
  'cash-web-taxes-open-for-data-entry': FeatureFlagDefaultStringVariant
  'cash-web-taxes-sam-restriction': boolean
  'cash-web-taxes-timeline-event-dates': Record<TimelineStage, string>
  'cash-web-taxes-timeline-stage': TimelineStage
  'cash-web-use-v2-cdp-endpoint': boolean
}
